import { useState } from "react";
import { LoadErrorState } from "../components/ui/LoadState";
import { isNotFoundError } from "../lib/load-state";
import { Link, useParams, useNavigate } from "react-router-dom";
import { useGate } from "../hooks/useGate";
import { useHumanGateAction } from "../hooks/useHumanGates";
import { Markdown } from "../components/agent/Markdown";
import { WorkspaceHeader } from "../components/layout/WorkspaceHeader";
import { timeAgo } from "../lib/utils";

/** Relative deadline label, e.g. "Due in 6h" or "Overdue" */
function relativeDeadline(timeoutAt: string | null): string | null {
  if (!timeoutAt) return null;
  const ms = new Date(timeoutAt).getTime() - Date.now();
  if (ms <= 0) return "Overdue";
  const h = Math.floor(ms / 3_600_000);
  const m = Math.floor((ms % 3_600_000) / 60_000);
  if (h >= 24) return `Due in ${Math.floor(h / 24)}d`;
  if (h > 0) return `Due in ${h}h`;
  return `Due in ${m}m`;
}

/** Human-readable platform name */
function platformLabel(platform: string | null | undefined): string {
  if (!platform) return "—";
  const map: Record<string, string> = {
    linkedin: "LinkedIn",
    twitter: "Twitter / X",
    instagram: "Instagram",
    reddit: "Reddit",
    facebook: "Facebook",
    tiktok: "TikTok",
    youtube: "YouTube",
    blog: "Blog",
    email: "Email",
  };
  return map[platform.toLowerCase()] ?? platform;
}

/** Human-readable format name */
function formatLabel(format: string | null | undefined): string {
  if (!format) return "—";
  return format.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
}

/** Extract draft markdown from preview */
function getDraft(preview: Record<string, unknown>): string | null {
  const draft = preview["outputs.draft"];
  return typeof draft === "string" && draft.trim() ? draft : null;
}

/** Extract voice review object from preview */
function getVoiceReview(preview: Record<string, unknown>): {
  score?: number;
  passed?: boolean;
  feedback?: string;
} | null {
  const vr = preview["outputs.voiceReview"];
  if (typeof vr === "object" && vr !== null) return vr as { score?: number; passed?: boolean; feedback?: string };
  return null;
}

/** Render campaign voice overrides as a readable list */
function VoiceRulesList({ overrides }: { overrides: Record<string, unknown> }) {
  const entries = Object.entries(overrides).filter(([, v]) => v !== null && v !== undefined && v !== "");
  if (entries.length === 0) return null;
  return (
    <ul className="mt-1 space-y-0.5 text-xs text-slate-600">
      {entries.map(([key, val]) => (
        <li key={key} className="flex gap-1">
          <span className="shrink-0 font-medium text-slate-500 capitalize">{key.replace(/_/g, " ")}:</span>
          <span>{typeof val === "boolean" ? (val ? "Yes" : "No") : String(val)}</span>
        </li>
      ))}
    </ul>
  );
}

function newIdempotencyKey(): string {
  return typeof crypto !== "undefined" && "randomUUID" in crypto
    ? crypto.randomUUID()
    : `idem-${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

export function GateReview() {
  const { gateId } = useParams<{ gateId: string }>();
  const navigate = useNavigate();
  const { data, isLoading, error, refetch } = useGate(gateId);
  const actionMut = useHumanGateAction();
  const [comment, setComment] = useState("");
  const [localError, setLocalError] = useState<string | null>(null);
  const [idempotencyKey] = useState(() => newIdempotencyKey());

  const gate = data?.data;

  async function submit(action: "approve" | "reject" | "changes") {
    if (!gate) return;
    setLocalError(null);
    if ((action === "reject" || action === "changes") && !comment.trim()) {
      setLocalError("A comment is required for this action.");
      return;
    }
    try {
      await actionMut.mutateAsync({
        gateId: gate.id,
        action,
        comment: action === "approve" ? undefined : comment.trim(),
        idempotencyKey,
      });
      navigate("/workflows");
    } catch {
      setLocalError("Action failed — please try again.");
    }
  }

  if (isLoading) {
    return (
      <div className="flex min-h-[60vh] items-center justify-center">
        <div className="h-8 w-8 animate-spin rounded-full border-2 border-brand-600 border-t-transparent" />
      </div>
    );
  }

  // "Not found" is only true for a 404; other failures say nothing about the approval.
  if (error && !isNotFoundError(error) && !gate) {
    return (
      <div className="space-y-4">
        <div className="mb-1 flex flex-wrap items-center gap-2 text-sm text-slate-500">
          <Link to="/workflows" className="hover:text-slate-800">Workflows</Link>
        </div>
        <LoadErrorState
          title="This approval could not be loaded"
          description="We could not reach the server. The approval may still be waiting for you."
          onRetry={() => void refetch()}
        />
      </div>
    );
  }

  if (error || !gate) {
    return (
      <div className="space-y-4">
        <div className="mb-1 flex flex-wrap items-center gap-2 text-sm text-slate-500">
          <Link to="/workflows" className="hover:text-slate-800">Workflows</Link>
        </div>
        <div className="rounded-[20px] border border-red-200 bg-red-50 px-4 py-8 text-center shadow-sm">
          <p className="font-medium text-red-800">Approval not found</p>
          <p className="mt-1 text-sm text-red-600">This approval may have already been resolved or the link is invalid.</p>
          <Link to="/workflows" className="mt-4 inline-block text-sm font-medium text-brand-700 hover:text-brand-800 hover:underline">
            Back to Workflows
          </Link>
        </div>
      </div>
    );
  }

  const draft = getDraft(gate.preview ?? {});
  const voiceReview = getVoiceReview(gate.preview ?? {});
  const deadline = relativeDeadline(gate.timeoutAt ?? null);
  const title = gate.briefTitle ?? gate.title;
  const isResolved = gate.status !== "pending";

  return (
    <div className="space-y-6">
      <WorkspaceHeader
        eyebrow="Operator Workspace"
        title={title}
        description="Review the generated draft, its voice context, and the posting constraints before approving or requesting changes."
        breadcrumbs={[
          { label: "Workspace", href: "/home" },
          { label: "Workflows", href: "/workflows" },
          { label: title },
        ]}
        metadata={(
          <>
            {deadline && (
              <span className={`rounded-full px-2.5 py-1 ring-1 ${deadline === "Overdue" ? "bg-red-50 text-red-600 ring-red-200" : "bg-white text-brand-700 ring-slate-200"}`}>
                {deadline}
              </span>
            )}
            <span className="rounded-full bg-white px-2.5 py-1 ring-1 ring-slate-200">
              {timeAgo(gate.createdAt)}
            </span>
          </>
        )}
      />

      {/* Two-column layout */}
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-[280px_1fr]">

        {/* ── Left: Context panel ── */}
        <aside className="space-y-5 lg:sticky lg:top-20 lg:self-start">

          {/* Campaign */}
          <div className="rounded-[20px] border border-slate-200 bg-white p-4 shadow-sm">
            <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-400">Campaign</p>
            <p className="font-medium text-slate-900">{gate.campaignTitle ?? "—"}</p>
            {gate.campaignGoal && (
              <p className="mt-1.5 text-sm text-slate-600 leading-relaxed">{gate.campaignGoal}</p>
            )}
          </div>

          {/* Posting context */}
          <div className="rounded-[20px] border border-slate-200 bg-white p-4 shadow-sm">
            <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-400">Posting</p>
            <div className="space-y-1.5 text-sm">
              <div className="flex items-center justify-between">
                <span className="text-slate-500">Platform</span>
                <span className="font-medium text-slate-800">{platformLabel(gate.briefPlatform)}</span>
              </div>
              {gate.briefFormat && (
                <div className="flex items-center justify-between">
                  <span className="text-slate-500">Format</span>
                  <span className="font-medium text-slate-800">{formatLabel(gate.briefFormat)}</span>
                </div>
              )}
            </div>
          </div>

          {/* Voice review */}
          {voiceReview && (
            <div className="rounded-[20px] border border-slate-200 bg-white p-4 shadow-sm">
              <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-400">Voice Review</p>
              <div className="flex items-center gap-2">
                <span className={`inline-flex h-2 w-2 rounded-full ${voiceReview.passed ? "bg-emerald-500" : "bg-amber-500"}`} />
                <span className="font-semibold text-slate-800">
                  {typeof voiceReview.score === "number" ? `${voiceReview.score}/100` : "—"}
                </span>
                <span className={`text-xs font-medium ${voiceReview.passed ? "text-emerald-600" : "text-amber-600"}`}>
                  {voiceReview.passed ? "Passed" : "Needs work"}
                </span>
              </div>
              {voiceReview.feedback && (
                <p className="mt-2 text-sm text-slate-600 leading-relaxed">"{voiceReview.feedback}"</p>
              )}
            </div>
          )}

          {/* Voice rules applied */}
          {gate.campaignVoiceOverrides && Object.keys(gate.campaignVoiceOverrides).length > 0 && (
            <div className="rounded-[20px] border border-slate-200 bg-white p-4 shadow-sm">
              <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-400">Voice Rules Applied</p>
              <VoiceRulesList overrides={gate.campaignVoiceOverrides} />
            </div>
          )}

          {/* Brand */}
          {gate.brandName && (
            <div className="rounded-[20px] border border-slate-200 bg-white p-4 shadow-sm">
              <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-400">Brand</p>
              <p className="font-medium text-slate-900">{gate.brandName}</p>
            </div>
          )}
        </aside>

        {/* ── Right: Draft content ── */}
        <div className="min-w-0 rounded-[24px] border border-slate-200 bg-white p-6 shadow-sm">
          {draft ? (
            <Markdown content={draft} />
          ) : (
            <pre className="whitespace-pre-wrap break-words font-sans text-xs text-slate-600">
              {JSON.stringify(gate.preview, null, 2)}
            </pre>
          )}
        </div>
      </div>

      {/* ── Actions / Resolved state ── */}
      {isResolved ? (
        <div className="rounded-[20px] border border-slate-200 bg-slate-50 p-5 text-center shadow-sm">
          <p className="font-medium text-slate-700">
            This approval has already been resolved
            {gate.resolvedAction ? ` (${gate.resolvedAction})` : ""}.
          </p>
          <Link to="/workflows" className="mt-3 inline-block text-sm font-medium text-brand-700 hover:text-brand-800 hover:underline">
            Back to Workflows
          </Link>
        </div>
      ) : (
        <div className="rounded-[20px] border border-slate-200 bg-white p-5 shadow-sm">
          <label htmlFor="gate-comment" className="mb-1.5 block text-sm font-medium text-slate-700">
            Comment <span className="text-slate-400">(required for Reject / Request changes)</span>
          </label>
          <textarea
            id="gate-comment"
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            rows={3}
            placeholder="Notes for the agent or content team..."
            className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm text-slate-800 placeholder-slate-400 focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500"
          />

          {(localError ?? actionMut.error) && (
            <p className="mt-1.5 text-sm text-red-600">
              {localError ?? (actionMut.error instanceof Error ? actionMut.error.message : "Action failed")}
            </p>
          )}

          <div className="mt-4 flex flex-wrap items-center justify-end gap-3">
            <Link to="/workflows" className="rounded-lg border border-slate-300 bg-white px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50">
              Cancel
            </Link>
            {gate.actions.includes("reject") && (
              <button
                type="button"
                disabled={actionMut.isPending}
                onClick={() => void submit("reject")}
                className="rounded-lg border border-red-300 bg-white px-4 py-2 text-sm font-medium text-red-700 hover:bg-red-50 disabled:opacity-50"
              >
                Reject
              </button>
            )}
            {gate.actions.includes("changes") && (
              <button
                type="button"
                disabled={actionMut.isPending}
                onClick={() => void submit("changes")}
                className="rounded-lg border border-amber-300 bg-white px-4 py-2 text-sm font-medium text-amber-700 hover:bg-amber-50 disabled:opacity-50"
              >
                Request changes
              </button>
            )}
            {gate.actions.includes("approve") && (
              <button
                type="button"
                disabled={actionMut.isPending}
                onClick={() => void submit("approve")}
                className="rounded-lg bg-slate-900 px-4 py-2 text-sm font-medium text-white hover:bg-slate-800 disabled:opacity-50"
              >
                {actionMut.isPending ? "Saving…" : "✓ Approve"}
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
